import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {ListResult, ProductExpando, SingleResult, WeeklyMetricsGroups, WeeklyMetricsParams} from 'utils/types';
import {addResultCases} from 'utils/helpers';
import {PAGE_SIZE_ALL} from 'components/table/table-hook';
import {metricNamesByGroup} from 'utils/constants';
import ApiService from 'redux/services/api-service';

export type WeeklyMetricsChartData = {
  datasets: Record<
    string,
    {
      data: {x: string | Date | null; y: number}[];
      group: string;
      name: string;
    }
  >;
};
export interface ItemState {
  modifyInProgress: boolean;
  listResult: ListResult<ProductExpando>;
  itemVariableFilters: any;
  itemVariableFiltersLoading: boolean;
  groupTotals: SingleResult<Record<string, number>>;
  [WeeklyMetricsGroups.upc_weekly_average_sales]: SingleResult<WeeklyMetricsChartData>;
  [WeeklyMetricsGroups.upc_weekly_total_sales]: SingleResult<WeeklyMetricsChartData>;
  [WeeklyMetricsGroups.upc_weekly_num_stores_selling]: SingleResult<WeeklyMetricsChartData>;
}

export const itemEndpoints = {
  getItems: createAsyncThunk('item/getItems', async (params: any) => {
    const {offset, limit, ordering, inline_attributes: attrs, ...filters} = params;
    const queryParams = {offset, limit, ordering, inline_attributes: attrs};
    if (queryParams.limit === PAGE_SIZE_ALL) {
      queryParams.offset = 0;
    }
    if ('upc' in filters && filters.upc.includes(',')) {
      filters.upc_in = filters.upc;
      delete filters.upc;
    }
    const response = await ApiService.post(`/items/?${new URLSearchParams(queryParams)}`, filters);
    return response.data;
  }),
  deleteItem: createAsyncThunk('item/deleteItems', async (id: number) => {
    const response = await ApiService.delete(`/items/${id}`);
    return response.data;
  }),
  getCampaignItemAttributes: createAsyncThunk('item/getCampaignItemAttributes', async (params: any) => {
    const response = await ApiService.get(`/campaign_item_variables/?${new URLSearchParams(params)}`);
    return response.data;
  }),
  updateItemBulk: createAsyncThunk('item/updateItemBulk', async (payload: any) => {
    const response = await ApiService.post('/bulk_item_update/', payload);
    return response.data;
  }),
  getGroupTotals: createAsyncThunk('item/getGroupTotals', async (params: {campaign_id: number | string}) => {
    const response = await ApiService.get(
      `/items/group_totals/?${new URLSearchParams({campaign_id: String(params.campaign_id)})}`,
    );
    return {value: response.data as Record<string, number>};
  }),
  getCampaignItemVariableFilters: createAsyncThunk('item/getCampaignItemVariableFilters', async (params: any) => {
    const response = await ApiService.get(`/campaign_item_variable_filters/?${new URLSearchParams(params)}`);
    return response.data;
  }),
  getWeeklyMetrics: createAsyncThunk('item/getWeeklyMetrics', async (params: any) => {
    const response = await ApiService.get(`/weekly_metrics/?${new URLSearchParams(params)}`);
    return response.data;
  }),
  getWeeklyMetricsChartDataAverageSales: createAsyncThunk(
    'item/getWeeklyMetricsChartDataAverageSales',
    async (params: WeeklyMetricsParams) => {
      const requestParams = {...params, name__in: metricNamesByGroup[WeeklyMetricsGroups.upc_weekly_average_sales]};
      const response = await ApiService.get(`/weekly_metrics/chart_data/?${new URLSearchParams(requestParams as any)}`);
      return {as: 'success', value: response.data};
    },
  ),
  getWeeklyMetricsChartDataTotalSales: createAsyncThunk(
    'item/getWeeklyMetricsChartDataTotalSales',
    async (params: WeeklyMetricsParams) => {
      const requestParams = {...params, name__in: metricNamesByGroup[WeeklyMetricsGroups.upc_weekly_total_sales]};
      const response = await ApiService.get(`/weekly_metrics/chart_data/?${new URLSearchParams(requestParams as any)}`);
      return {as: 'success', value: response.data};
    },
  ),
  getWeeklyMetricsChartDataNumStoresSelling: createAsyncThunk(
    'item/getWeeklyMetricsChartDataNumStoresSelling',
    async (params: WeeklyMetricsParams) => {
      const requestParams = {
        ...params,
        name__in: metricNamesByGroup[WeeklyMetricsGroups.upc_weekly_num_stores_selling],
      };
      const response = await ApiService.get(`/weekly_metrics/chart_data/?${new URLSearchParams(requestParams as any)}`);
      return {as: 'success', value: response.data};
    },
  ),
};
export const itemSlice = createSlice({
  name: 'item',
  initialState: {
    modifyInProgress: false,
    itemVariableFilters: {},
    itemVariableFiltersLoading: false,
    listResult: {as: 'idle'},
    groupTotals: {as: 'idle'},
    [WeeklyMetricsGroups.upc_weekly_average_sales]: {as: 'idle'},
    [WeeklyMetricsGroups.upc_weekly_total_sales]: {as: 'idle'},
    [WeeklyMetricsGroups.upc_weekly_num_stores_selling]: {as: 'idle'},
  } as ItemState,
  reducers: {},
  extraReducers: builder => {
    addResultCases(builder, itemEndpoints.getItems, 'listResult');
    addResultCases(builder, itemEndpoints.getGroupTotals, 'groupTotals');
    builder
      .addCase(itemEndpoints.deleteItem.fulfilled, (state, {payload}) => {
        if (state.listResult.as !== 'success') return;

        const list = [...state.listResult.results];
        const index = list.findIndex(it => it.id === payload);
        if (index === -1) return;

        list.splice(index, 1);
        state.listResult.results = list;
        state.listResult.count -= 1;
      })
      .addCase(itemEndpoints.updateItemBulk.pending, state => {
        state.modifyInProgress = true;
      })
      .addCase(itemEndpoints.updateItemBulk.fulfilled, state => {
        state.modifyInProgress = false;
      })
      .addCase(itemEndpoints.updateItemBulk.rejected, state => {
        state.modifyInProgress = false;
      })
      .addCase(itemEndpoints.getCampaignItemVariableFilters.pending, state => {
        state.itemVariableFiltersLoading = true;
      })
      .addCase(itemEndpoints.getCampaignItemVariableFilters.fulfilled, (state, {payload}) => {
        state.itemVariableFiltersLoading = false;
        state.itemVariableFilters = payload;
      });
    addResultCases(
      builder,
      itemEndpoints.getWeeklyMetricsChartDataAverageSales,
      WeeklyMetricsGroups.upc_weekly_average_sales as string as keyof ItemState,
    );
    addResultCases(
      builder,
      itemEndpoints.getWeeklyMetricsChartDataTotalSales,
      WeeklyMetricsGroups.upc_weekly_total_sales as string as keyof ItemState,
    );
    addResultCases(
      builder,
      itemEndpoints.getWeeklyMetricsChartDataNumStoresSelling,
      WeeklyMetricsGroups.upc_weekly_num_stores_selling as string as keyof ItemState,
    );
  },
});

export const itemActions = itemSlice.actions;

export type ItemSlice = {
  [itemSlice.name]: ReturnType<(typeof itemSlice)['reducer']>;
};
